import React, {useEffect, useRef} from "react";
import {createPortal} from "react-dom";
import {columns as baseColumns} from "@/features/types/AutoTableInterface";

import Select, {components as selectComponents, MultiValue, SelectInstance, SingleValue} from "react-select";
import {Input} from "@/shared/components/ui/input";
import {DateRangePicker} from "@/shared/components/ui/date-range-picker";
import {Checkbox} from "@/shared/components/ui/checkbox";
import {Label} from "@/shared/components/ui/label";
import {format} from "date-fns";
import {selectShadcnStyles} from "@/shared/lib/selectShadcnStyles";
import AsyncSelect from "react-select/async";
import {debounce} from "@tanstack/virtual-core";
import {getAutoTableOptionsWithFilters} from "@/features/controllers/AutoTableController";
import {
    FilterKey,
    Filters,
    GroupedOptions,
    nullableOptions,
    numberInputOptions,
    Option
} from "@/features/types/AutoTableFilterInterface";
import {HorizontalTab} from "@/shared/components/HorizontalTab";
import {ChevronDown, X} from "lucide-react";

interface FilterToolboxProps {
    columnId: FilterKey;
    filters: Filters;
    position: { x: number; y: number };
    onChange: (value: string) => void;
    onClose: () => void;
}

export const FilterToolbox: React.FC<FilterToolboxProps> = ({
                                                                columnId,
                                                                filters,
                                                                position,
                                                                onChange,
                                                                onClose,
                                                            }) => {
    const column = React.useMemo(() => baseColumns.find(c => c.accessorKey === columnId), [columnId]);
    const filterType = column?.type ?? "input";
    const rawValue: string = filters[columnId] ?? "";

    const [defaultDynamicOptions, setDefaultDynamicOptions] = React.useState<Option[]>([]);
    const [selectOptions, setSelectOptions] = React.useState<Option[]>([]);
    const [menuIsOpen, setMenuIsOpen] = React.useState(true);
    const [selectPanel, setSelectPanel] = React.useState<"add" | "selected">("add");
    const [adjustedPosition, setAdjustedPosition] = React.useState(position);
    const [isLoadingOptions, setIsLoadingOptions] = React.useState(false);
    const [isPanelAnimating, setIsPanelAnimating] = React.useState(false);

    const ref = React.useRef<HTMLDivElement>(null);
    const asyncSelectRef = useRef<SelectInstance<Option, true> | null>(null);

    const GroupHeading = () => (
        <hr
            style={{
                border: "none",
                borderTop: "1px solid #777",
            }}
        />
    );

    const groupedOptions: GroupedOptions = [
        {
            label: "static",
            options: nullableOptions
        },
        {
            label: "dynamic",
            options: selectOptions
        }
    ];

    const selectedSelectOptions = React.useMemo<Option[]>(() => {
        if (filterType !== "select" || !rawValue) return [];

        const nullableMatch = nullableOptions.find((o) => o.value === rawValue);
        if (nullableMatch) return [nullableMatch];

        if (rawValue.startsWith("[")) {
            try {
                const parsed = JSON.parse(rawValue);
                if (Array.isArray(parsed)) {
                    return parsed
                        .map((item) => String(item ?? "").trim())
                        .filter(Boolean)
                        .map((value) => ({value, label: value}));
                }
            } catch {
            }
        }

        return [{value: rawValue, label: rawValue}];
    }, [filterType, rawValue]);
    const selectedSelectCount = selectedSelectOptions.length;
    const toolboxWidth = filterType === "select" ? 280 : 260;
    const selectViewportHeight = React.useMemo(() => {
        if (selectPanel === "selected") return 240;
        return menuIsOpen ? 380 : 200;
    }, [menuIsOpen, selectPanel]);
    const selectTabs = React.useMemo(
        () => [
            {id: "add", label: "Wybierz"},
            {id: "selected", label: `Lista elem. (${selectedSelectCount})`},
        ],
        [selectedSelectCount]
    );
    const currentPanelProgress = selectPanel === "add" ? 0 : 1;
    const canShowSelectMenu = selectPanel === "add" && !isPanelAnimating;

    const applySelectedOptions = (values: string[]) => {
        const normalized = values
            .map((value) => String(value ?? "").trim())
            .filter(Boolean);

        if (normalized.length === 0) {
            onChange("");
            return;
        }

        const nullableValues = new Set(nullableOptions.map((item) => item.value));
        const nullableRemaining = normalized.filter((value) => nullableValues.has(value));
        if (nullableRemaining.length > 0) {
            onChange(nullableRemaining[nullableRemaining.length - 1]);
            return;
        }

        onChange(JSON.stringify(Array.from(new Set(normalized))));
    };

    const switchSelectPanel = (nextPanel: "add" | "selected") => {
        if (nextPanel === selectPanel) {
            return;
        }
        setIsPanelAnimating(true);
        setSelectPanel(nextPanel);
        const shouldOpenMenu = nextPanel === "add";
        setMenuIsOpen(shouldOpenMenu);

        if (shouldOpenMenu) {
            setTimeout(() => {
                asyncSelectRef.current?.focus();
            }, 0);
        }
    };

    const removeSelectedValue = (valueToRemove: string) => {
        const remaining = selectedSelectOptions
            .filter((item) => item.value !== valueToRemove)
            .map((item) => item.value);
        applySelectedOptions(remaining);
    };

    const handleDropdownToggle = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setMenuIsOpen((prev) => !prev);
        setTimeout(() => {
            asyncSelectRef.current?.focus();
        }, 0);
    };

    useEffect(() => {
        if (!column) return;
        if (filterType !== "numberInput") return;

        if (filterType === "numberInput") {
            setSelectOptions(numberInputOptions)
        }
    }, [column, filterType, filters]);

    useEffect(() => {
        if (filterType === "select" && asyncSelectRef.current) {
            asyncSelectRef.current.focus();
        }
    }, [filterType]);

    useEffect(() => {
        if (filterType !== "select") return;
        setSelectPanel("add");
        setMenuIsOpen(true);
    }, [columnId, filterType]);

    useEffect(() => {
        if (filterType !== "select") return;
        const timeout = setTimeout(() => {
            setIsPanelAnimating(false);
        }, 230);

        return () => clearTimeout(timeout);
    }, [selectPanel, filterType]);

    useEffect(() => {
        if (filterType !== "select") return;

        setIsLoadingOptions(true);
        (async () => {
            try {
                // Przekazujemy wszystkie filtry - backend wykluczy targetAlias z filtrów
                // i nie doda LIKE (bo isSearch = false), więc pokaże wszystkie opcje
                const opts = await getAutoTableOptionsWithFilters(columnId)(filters, false);
                setDefaultDynamicOptions(opts);
            } catch {
            } finally {
                setIsLoadingOptions(false);
            }
        })();
    }, [columnId, filters, filterType]);

    const loadDynamicOptions = React.useMemo(
        () => debounce(window, async (inputValue: string, callback: (opts: Option[]) => void) => {
            try {
                const searchFilters: Record<string, string> = {};
                
                Object.entries(filters).forEach(([key, value]) => {
                    if (key !== columnId) {
                        searchFilters[key] = String(value ?? '');
                    }
                });
                
                searchFilters[columnId] = inputValue || '';
                
                const opts = await getAutoTableOptionsWithFilters(columnId)(searchFilters, true);
                callback(opts.slice(0, 15));
            } catch {
                callback([]);
            }
        }, 300),
        [columnId, filters]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onClose]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [onClose]);

    useEffect(() => {
        if (ref.current) {
            const {innerWidth} = window;
            const width = toolboxWidth;
            let newX = position.x - (width / 2);
            if (newX + width > innerWidth) newX = innerWidth - width - 10;
            if (newX < 0) newX = 10;
            setAdjustedPosition({x: newX, y: position.y + 6});
        }
    }, [position, toolboxWidth]);

    return createPortal(
        <div
            ref={ref}
            className="fixed z-50 border border-gray-500 rounded-xl shadow-lg p-4"
            style={{
                top: adjustedPosition.y,
                left: adjustedPosition.x,
                width: toolboxWidth,
                backgroundColor: 'rgba(127, 127, 127, 0.5)',
                backdropFilter: 'blur(1px)',
            }}
        >
            {filterType === "input" && (
                <Input
                    key={columnId}
                    type="text"
                    placeholder={column?.header}
                    value={rawValue}
                    onChange={(e) => onChange(e.target.value)}
                    className="px-2 py-1 border rounded text-sm bg-white"
                />
            )}

            {filterType === "numberInput" && (() => {
                const parsed = (() => {
                    try {
                        return rawValue ? JSON.parse(rawValue) : {value: "", option: ">"};
                    } catch {
                        return {value: rawValue ?? "", option: rawValue ?? ">"};
                    }
                })();
                const isNullableOption = nullableOptions.some(o => o.value === parsed.option);

                return (
                    <div
                        className="
                            flex items-center h-9 rounded-md bg-white
                            border border-[#e5e5e5] transition-all
                            shadow-sm
                            focus-within:ring-2 focus-within:ring-primary/30
                            focus-within:border-primary
                            hover:border-[#d4d4d4]
                          "
                    >
                        <div
                            className="flex-1 rounded-sm">
                            <Select
                                classNamePrefix="filter-select"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        border: "none",
                                        boxShadow: "none",
                                        backgroundColor: "transparent",
                                        "&:hover": {
                                            backgroundColor: "rgba(0,0,0,0.02)",
                                        },
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        width: '150%',
                                    }),
                                    group: (base) => ({
                                        ...base,
                                        padding: 0,
                                        margin: 0,
                                    }),
                                    menuList: (base) => ({
                                        ...base,
                                        paddingTop: 0,
                                        paddingBottom: 0,
                                    }),
                                }}
                                isSearchable={false}
                                options={groupedOptions}
                                components={{GroupHeading}}
                                value={
                                    selectOptions.find(o => o.value === parsed.option) ||
                                    nullableOptions.find(o => o.value === parsed.option) ||
                                    {value: parsed.option, label: parsed.option}
                                }
                                onChange={(opt: SingleValue<Option>) => {
                                    const newOption = opt?.value ?? ">";
                                    // Nie wywołuj onChange jeśli:
                                    // 1. Opcja się nie zmieniła
                                    // 2. Opcja to operator (>, <, =, !=) i input jest pusty (nie ma sensu filtrować pustą wartość)
                                    const isNullableOption = nullableOptions.some(o => o.value === newOption);
                                    const isOperatorOption = ['>', '<', '=', '!='].includes(newOption);
                                    const hasValue = parsed.value && parsed.value.trim() !== '';
                                    
                                    // Wywołaj onChange tylko jeśli:
                                    // - opcja to nullable (PUSTE/NIEPUSTE) - zawsze ma sens
                                    // - opcja to operator i input ma wartość
                                    // - opcja się zmieniła (dla bezpieczeństwa)
                                    if (newOption !== parsed.option) {
                                        if (isNullableOption || (isOperatorOption && hasValue)) {
                                            onChange(JSON.stringify({
                                                value: parsed.value,
                                                option: newOption
                                            }));
                                        }
                                    }
                                }}
                                noOptionsMessage={() => "Brak dostępnych opcji"}
                                loadingMessage={() => "Wczytywanie.."}
                            />
                        </div>
                        <div className="h-full border-l-5 border-gray-200"/>

                        <Input
                            value={parsed.value}
                            onChange={(e) => onChange(JSON.stringify({value: e.target.value, option: parsed.option}))}
                            placeholder={column?.header}
                            disabled={isNullableOption}
                            className="flex-1 text-sm border-none outline-none focus-visible:ring-0 focus:outline-none"
                        />
                    </div>
                );
            })()}

            {filterType === "select" && (
                <div className="space-y-2">
                    <div>
                        <HorizontalTab
                            tabs={selectTabs}
                            activeTab={selectPanel}
                            onTabChange={(tabId) => switchSelectPanel(tabId as "add" | "selected")}
                            size="md"
                            variant="switcher"
                            swipeable
                        />
                    </div>

                    <div
                        className="overflow-hidden"
                        style={{
                            height: selectViewportHeight,
                            transition: "height 150ms ease",
                        }}
                    >
                        <div
                            className="flex w-[200%] will-change-transform"
                            style={{
                                transform: `translateX(-${currentPanelProgress * 50}%)`,
                                transition: "transform 220ms ease-out",
                            }}
                        >
                            <div className="w-1/2 shrink-0">
                                <AsyncSelect<Option, true>
                                    ref={asyncSelectRef}
                                    key={columnId}
                                    placeholder={column?.header}
                                    isMulti
                                    isClearable={false}
                                    closeMenuOnSelect={false}
                                    openMenuOnClick={false}
                                    openMenuOnFocus={false}
                                    controlShouldRenderValue={false}
                                    cacheOptions
                                    defaultOptions={
                                        isLoadingOptions
                                            ? [{label: "static", options: nullableOptions}]
                                            : [
                                                {label: "static", options: nullableOptions},
                                                {label: "dynamic", options: defaultDynamicOptions}
                                            ]
                                    }
                                    isLoading={isLoadingOptions}
                                    className="basic-single w-full text-[14px]"
                                    menuIsOpen={menuIsOpen && canShowSelectMenu}
                                    onMenuOpen={() => {
                                    }}
                                    onMenuClose={() => {
                                    }}
                                    menuPortalTarget={
                                        canShowSelectMenu && typeof document !== "undefined" ? document.body : undefined
                                    }
                                    components={{
                                        GroupHeading,
                                        IndicatorSeparator: () => null,
                                        DropdownIndicator: (props) => (
                                            <selectComponents.DropdownIndicator
                                                {...props}
                                                innerProps={{
                                                    ...props.innerProps,
                                                    onMouseDown: (event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                    },
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={handleDropdownToggle}
                                                    className="inline-flex items-center justify-center rounded p-0.5 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                                                    aria-label={menuIsOpen ? "Zwiń listę opcji" : "Rozwiń listę opcji"}
                                                >
                                                    <ChevronDown
                                                        size={14}
                                                        className={`transition-transform ${menuIsOpen ? "rotate-180" : ""}`}
                                                    />
                                                </button>
                                            </selectComponents.DropdownIndicator>
                                        ),
                                    }}
                                    styles={{
                                        control: (base, props) => ({
                                            ...(selectShadcnStyles.control ? selectShadcnStyles.control(base, props) : base),
                                            minHeight: 38,
                                            alignItems: "center",
                                        }),
                                        group: (base, props) => ({
                                            ...(selectShadcnStyles.group ? selectShadcnStyles.group(base, props) : base),
                                            padding: 0,
                                            margin: 0
                                        }),
                                        menuList: (base, props) => ({
                                            ...(selectShadcnStyles.menuList ? selectShadcnStyles.menuList(base, props) : base),
                                            maxHeight: 340,
                                            fontSize: 14,
                                            paddingTop: 0,
                                            paddingBottom: 0,
                                        }),
                                        menu: (base, props) => ({
                                            ...(selectShadcnStyles.menu ? selectShadcnStyles.menu(base, props) : base),
                                            marginTop: 6,
                                            zIndex: 80,
                                        }),
                                        menuPortal: (base, props) => ({
                                            ...(selectShadcnStyles.menuPortal ? selectShadcnStyles.menuPortal(base, props) : base),
                                            zIndex: 85,
                                        }),
                                        valueContainer: (base, props) => ({
                                            ...(selectShadcnStyles.valueContainer ? selectShadcnStyles.valueContainer(base, props) : base),
                                            paddingTop: 0,
                                            paddingBottom: 0,
                                        }),
                                        input: (base, props) => ({
                                            ...(selectShadcnStyles.input ? selectShadcnStyles.input(base, props) : base),
                                            margin: 0,
                                            fontSize: 14,
                                            paddingTop: 0,
                                            paddingBottom: 0,
                                        }),
                                        indicatorsContainer: (base, props) => ({
                                            ...(selectShadcnStyles.indicatorsContainer ? selectShadcnStyles.indicatorsContainer(base, props) : base),
                                            alignSelf: "stretch",
                                        }),
                                        dropdownIndicator: (base, props) => ({
                                            ...(selectShadcnStyles.dropdownIndicator ? selectShadcnStyles.dropdownIndicator(base, props) : base),
                                            padding: 6,
                                        }),
                                    }}
                                    loadOptions={(inputValue) =>
                                        new Promise((resolve) => {
                                            loadDynamicOptions(inputValue, (dynamicOpts: Option[]) => {
                                                resolve([
                                                    {label: "static", options: nullableOptions},
                                                    {label: "dynamic", options: dynamicOpts}
                                                ]);
                                            });
                                        })
                                    }
                                    value={selectedSelectOptions}
                                    onChange={(opts: MultiValue<Option>) => {
                                        const selected = Array.from(opts ?? []);
                                        const values = selected
                                            .map((opt) => String(opt.value ?? "").trim())
                                            .filter(Boolean);
                                        applySelectedOptions(values);
                                    }}
                                    loadingMessage={() => "Wczytywanie..."}
                                    noOptionsMessage={() => "Brak dostępnych opcji"}
                                />
                            </div>

                            <div className="w-1/2 shrink-0">
                                <div className="overflow-hidden rounded-md border border-[#e2e8f0] bg-white shadow-sm">
                                    {selectedSelectCount === 0 ? (
                                        <div className="px-3 py-2 text-[14px] text-muted-foreground">
                                            Brak wybranych elementów.
                                        </div>
                                    ) : (
                                        <div className="max-h-[260px] overflow-y-auto">
                                            {selectedSelectOptions.map((item) => (
                                                <div
                                                    key={item.value}
                                                    className="flex items-center justify-between gap-2 border-b border-[#eef2f7] px-3 py-2 text-[14px] hover:bg-[#eaf3fb]"
                                                    title={item.label}
                                                >
                                                    <span className="truncate text-foreground">{item.label}</span>
                                                    <button
                                                        type="button"
                                                        className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-red-100 hover:text-red-600"
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            removeSelectedValue(item.value);
                                                        }}
                                                        aria-label={`Usuń ${item.label}`}
                                                    >
                                                        <X size={14}/>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {filterType === "date" && (() => {
                let initialRange: { from?: Date; to?: Date } | undefined;
                if (rawValue?.startsWith("{")) {
                    try {
                        const parsed = JSON.parse(rawValue);
                        initialRange = {
                            from: parsed.from ? new Date(parsed.from) : undefined,
                            to: parsed.to ? new Date(parsed.to) : undefined,
                        };
                    } catch {
                    }
                }
                return (
                    <div onMouseDown={(e) => e.stopPropagation()}>
                        <DateRangePicker
                            key={columnId}
                            initialDateFrom={initialRange?.from}
                            initialDateTo={initialRange?.to}
                            onUpdate={({range}) => {
                                const {from, to} = range;

                                if (from && to) {
                                    onChange(JSON.stringify({
                                        from: range.from ? format(range.from, "yyyy-MM-dd") : null,
                                        to: range.to ? format(range.to, "yyyy-MM-dd") : null
                                    }));
                                    return;
                                } else if (from && !to) {
                                    onChange(JSON.stringify({
                                        from: range.from ? format(range.from, "yyyy-MM-dd") : null,
                                        to: null
                                    }));
                                    return;
                                } else if (!from && to) {
                                    onChange(JSON.stringify({
                                        from: null,
                                        to: range.to ? format(range.to, "yyyy-MM-dd") : null
                                    }));
                                    return;
                                } else {
                                    onChange(JSON.stringify({from: null, to: null}));
                                    return;
                                }
                            }}
                            align="start"
                            locale="pl-PL"
                            showCompare={false}
                        />
                    </div>
                );
            })()}

            {filterType === "boolean" && (
                <div className="flex items-center gap-3">
                    <Checkbox
                        key={columnId}
                        checked={rawValue === "1"}
                        onCheckedChange={(checked) => onChange(checked ? "1" : "0")}
                        className="bg-white"
                    />
                    <Label htmlFor={columnId}
                           className="text-white"> Czy {column?.header.toLowerCase()}? </Label>

                </div>
            )}
        </div>,
        document.body
    );
};
